import React from 'react';

import { getValueId } from '@/utils/categUtils';
import { isObjectEmpty } from '@/utils/comparisonUtils';

import Tile from '@/components/common/Tile';

import styles from './BlocFilter.module.scss';
import Icon from '@/components/common/iconedLinks/Icon';
import { URLS } from '@/constants/urls';

type FilterType = {
  nameId?: string;
  nbByValue?: any[];
  type?: string;
  valuedId?: string;
}

type BlocFilterProps = {
  filter: FilterType;
  canSelectMultiple?: boolean;
  onChange: (arg: any) => void;
  selectedValue: any;
  className?: string;
}

const OTHER_FORMAT = {
  VALUE_ID: 'X',
  SHORT_VALUE: '--',
  JEROBOAM_VALUE_ID: 'F',
  JEROBOAM_SHORT_VALUE: '3L - 5L'
};

const LITRAGE_UNIT = {
  CL: 'CL',
  L: 'L'
};

type RED_ATTRIBUTE_VALUE_ID = '8';
type WHITE_ATTRIBUTE_VALUE_ID = '5';
type PINK_ATTRIBUTE_VALUE_ID = '2';
type MIXED_ATTRIBUTE_VALUE_ID = '9';
type AMBER_ATTRIBUTE_VALUE_ID = '19';

type COLOR_ATTRIBUTE_VALUE_ID = RED_ATTRIBUTE_VALUE_ID | WHITE_ATTRIBUTE_VALUE_ID
  | PINK_ATTRIBUTE_VALUE_ID | MIXED_ATTRIBUTE_VALUE_ID | AMBER_ATTRIBUTE_VALUE_ID;

const BlocFilter = ({
  filter,
  onChange,
  selectedValue = {},
  canSelectMultiple = false,
  className
}: BlocFilterProps): React.JSX.Element => {
  const { nameId, nbByValue } = filter;
  // TODO: a better way to identify multi label bloc filter from single label bloc filter
  const isMultiLabel = ['format'].includes(nameId);
  const isColorLabel = ['couleur'].includes(nameId);

  const blocLabel = ({ value, shortValue, nb = null }) => isMultiLabel ? (
    { paragraph: value, subParagraph: shortValue }
  ) : isColorLabel ? (
    { paragraph: `${value} (${nb})` }
  ) : (
    { paragraph: value }
  );

  const handleClick = (key, value) => {
    if (canSelectMultiple) {
      let multiSelect = {};

      if (Object.prototype.hasOwnProperty.call(selectedValue, key)) {
        for (const [_key, _value] of Object.entries(selectedValue)) {
          if (_key !== key) {
            multiSelect[_key] = _value;
          }
        }
      } else {
        multiSelect = { ...selectedValue, [key]: value };
      }

      onChange(Object.values(multiSelect).map((value) => ({
        nameId,
        valueId: isObjectEmpty(multiSelect) ? undefined : getValueId(value, nbByValue),
        value: isObjectEmpty(multiSelect) ? undefined : value,
        check: !isObjectEmpty(multiSelect)
      })));
    } else {
      const uniqueSelect = selectedValue[key] === value ? {} : { [key]: value };
      const newValue = uniqueSelect[key];
      onChange({
        nameId,
        valueId: getValueId(newValue, nbByValue),
        value: newValue,
        check: !!newValue
      });
    }
  };

  const finalNbByValue = [...nbByValue].sort((a, b) => {
    if (nameId === 'format') {
      if (a.valueId === OTHER_FORMAT.VALUE_ID) return 1;
      if (b.valueId === OTHER_FORMAT.VALUE_ID) return -1;

      const extractLitrage = (value) => {
        const [number, unit] = value.shortValue?.match(new RegExp(`([\\d.]+)(${LITRAGE_UNIT.CL}|${LITRAGE_UNIT.L})`, 'i')).slice(1) || [null, null];
        return unit?.toUpperCase() === LITRAGE_UNIT.CL ? parseInt(number) : parseInt(number) * 100;
      };

      return extractLitrage(a) - extractLitrage(b);
    }
  });

  const icon = (valueId: COLOR_ATTRIBUTE_VALUE_ID) => {
    if (filter.nameId === 'couleur') {
      switch (valueId) {
        case '8':
          return <Icon src={URLS.FACET_RED} alt="black" width={24} height={24} />;
        case '5':
          return <Icon src={URLS.FACET_WHITE} alt="white" width={24} height={24} />;
        case '2':
          return <Icon src={URLS.FACET_ROSE} alt="orange" width={24} height={24} />;
        case '9':
          return <Icon src={URLS.FACET_BICOLOR} alt="demeter" width={24} height={24} />;
        case '19':
          return <Icon src={URLS.FACET_AMBER} alt="hvac" width={24} height={24} />;
        default: return null;
      }
    }
  };

  const containerClassName = [
    styles.container,
    nameId === 'couleur' ? styles['color-filter'] : 'row',
    className
  ].filter(Boolean).join(' ');

  return finalNbByValue?.length > 0 ? (
    <div className={containerClassName} data-rbf="bloc-filter" data-test-id="bloc-filter">
      {finalNbByValue.map((filterValue) => {
        const { value, valueId } = filterValue;
        const key = `${nameId}-${valueId}`;
        const isOtherFormat = ['format'].includes(nameId) && valueId.toLowerCase() === OTHER_FORMAT.VALUE_ID.toLowerCase();
        const isJeroboam = ['format'].includes(nameId) && valueId.toLowerCase() === OTHER_FORMAT.JEROBOAM_VALUE_ID.toLowerCase();
        const computedContent = isOtherFormat ? (
          blocLabel({ value, shortValue: OTHER_FORMAT.SHORT_VALUE })
        ) : (
          isJeroboam ? (
            blocLabel({ value, shortValue: OTHER_FORMAT.JEROBOAM_SHORT_VALUE })
          ) : (
            blocLabel(filterValue)
          )
        );

        return (
          <div
            data-test-id="bloc-filter-element"
            key={key}
            onClick={() => handleClick(key, value)}
            className={`col-${isMultiLabel ? '6' : '4'} p-0`}
          >
            <div className={styles.content} data-test-id="bloc-filter-item">
              <Tile
                content={computedContent}
                selected={selectedValue[key] === value}
                centered={!['format', 'couleur'].includes(nameId)}
                truncated={['format'].includes(nameId)}
                trailingContent={icon(valueId)}
              />
            </div>
          </div>
        );
      })}
    </div>
  ) : null;
};

export default BlocFilter;
